import { useEffect, useState } from "react";
import usePermissionStore from "@/shared/store/usePermissionStore";

interface AccessReturnType {
  allow_add: boolean | undefined | null;
  allow_delete: boolean | undefined | null;
  allow_print: boolean | undefined | null;
  allow_update: boolean | undefined | null;
  allow_view: boolean | undefined | null;
}

type PermissionAction =
  | "allow_add"
  | "allow_delete"
  | "allow_print"
  | "allow_update"
  | "allow_view";

function normalizePath(p: string) {
  if (!p) return "/";
  let path = p.startsWith("/") ? p : `/${p}`;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

function findBestMatch(
  path: string,
  featureAccess: Record<string, AccessReturnType> | null
): { key: string; access: AccessReturnType } | null {
  if (!featureAccess) return null;
  const normalizedPath = normalizePath(path);
  let bestKey: string | null = null;
  for (const key of Object.keys(featureAccess)) {
    const normalizedKey = normalizePath(key);
    if (
      normalizedPath === normalizedKey ||
      normalizedPath.startsWith(normalizedKey + "/") ||
      (normalizedKey === "/" && normalizedPath === "/")
    ) {
      if (!bestKey || normalizedKey.length > bestKey.length) {
        bestKey = normalizedKey;
      }
    }
  }

  if (!bestKey) return null;
  return { key: bestKey, access: featureAccess[bestKey] };
}

/**
 * Synchronous check that reads from the in-memory permission store.
 */
export function canAccess(path: string, action: PermissionAction): boolean {
  const normalizedPath = normalizePath(path);
  if (
    normalizedPath === "/" ||
    normalizedPath === "/dashboard" ||
    normalizedPath === "/login"
  )
    return true;

  const { featureAccess, isLoaded } = usePermissionStore.getState();

  if (!isLoaded || !featureAccess) return false;

  const match = findBestMatch(normalizedPath, featureAccess);
  if (!match) return false;

  return !!match.access[action];
}

/**
 * React hook for permission checking.
 * Subscribes to the in-memory permission store.
 */
export default function usePermission(
  path: string | undefined,
  action: PermissionAction
): { allowed: boolean; loading: boolean } {
  const { featureAccess, isLoaded, isLoading } = usePermissionStore();

  const [allowed, setAllowed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (typeof path === "undefined" || path === null) {
      setLoading(true);
      setAllowed(false);
      return;
    }

    if (!isLoaded) {
      setLoading(isLoading || !isLoaded);
      setAllowed(false);
      return;
    }

    setLoading(false);
    const result = canAccess(path, action);
    setAllowed(result);
  }, [path, action, featureAccess, isLoaded, isLoading]);

  return { allowed, loading };
}
